import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { PawPrint, CalendarDays, Syringe, MapPin } from "lucide-react";
import { api, fileUrl } from "@/lib/api";

export default function PublicPetRecord() {
  const { shareId } = useParams();
  const [rec, setRec] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/p/${shareId}`).then((r) => setRec(r.data)).catch(() => setNotFound(true));
  }, [shareId]);

  if (notFound) return <div className="min-h-[70vh] flex items-center justify-center text-brand-soft/60">Record not found.</div>;
  if (!rec) return <div className="min-h-[70vh] flex items-center justify-center text-brand-soft/60">Loading…</div>;

  const p = rec.pet || {};

  return (
    <div className="min-h-screen bg-brand-cream text-brand-soft print:bg-white" data-testid="public-pet-record">
      <div className="max-w-3xl mx-auto px-6 py-10 sm:py-14">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-2 text-brand-maroon">
            <PawPrint className="w-5 h-5" />
            <div className="font-serif text-xl">Furshala</div>
          </div>
          <button onClick={() => window.print()} className="text-xs text-brand-soft/60 underline print:hidden">Print</button>
        </div>

        <div className="bg-white rounded-3xl border border-brand-maroon/10 p-6 sm:p-10">
          {/* Pet header */}
          <div className="flex items-center gap-5">
            <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl bg-brand-ivory overflow-hidden flex-shrink-0">
              {p.photo ? <img src={fileUrl(p.photo)} alt={p.name} className="w-full h-full object-cover" /> : <PawPrint className="w-10 h-10 text-brand-maroon/40 m-auto mt-8" />}
            </div>
            <div className="min-w-0">
              <div className="eyebrow mb-1">Pet passport</div>
              <h1 className="font-serif text-3xl sm:text-4xl text-brand-soft leading-tight" data-testid="public-pet-name">{p.name || "Unnamed"}</h1>
              <div className="mt-1.5 text-sm text-brand-soft/70">
                {[p.species, p.breed, p.gender].filter(Boolean).join(" · ")}
              </div>
              {rec.owner_name && (
                <div className="mt-2 text-xs text-brand-soft/60 inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {rec.owner_name}{rec.owner_city ? `, ${rec.owner_city}` : ""}</div>
              )}
            </div>
          </div>

          {/* Pet details grid */}
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-4">
            <Info label="Weight" value={p.weight} />
            <Info label="Colour" value={p.colour} />
            <Info label="Date of birth" value={p.dob} />
            {p.microchip && <Info label="Microchip" value={p.microchip} />}
          </div>

          {/* Vaccinations */}
          <div className="mt-10">
            <div className="flex items-center gap-2 mb-4">
              <Syringe className="w-4 h-4 text-brand-maroon" />
              <div className="font-serif text-xl text-brand-soft">Vaccinations</div>
            </div>
            {(rec.vaccinations || []).length === 0 ? (
              <div className="text-sm text-brand-soft/50 italic">No vaccinations recorded yet.</div>
            ) : (
              <ul className="divide-y divide-brand-maroon/10">
                {rec.vaccinations.map((v) => (
                  <li key={v.id} className="py-3 flex items-center justify-between gap-3" data-testid={`public-vac-${v.id}`}>
                    <div>
                      <div className="text-sm font-medium text-brand-soft">{v.name}</div>
                      <div className="text-xs text-brand-soft/60 inline-flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" /> Given {v.date_given}</div>
                    </div>
                    {v.next_due && <div className="text-xs bg-brand-mustard/20 text-brand-maroon px-2.5 py-1 rounded-full">Next due {v.next_due}</div>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="text-center text-[11px] text-brand-soft/40 mt-6 print:hidden">
          Powered by <span className="text-brand-maroon">Furshala</span> · A lifelong pet record
        </div>
      </div>
    </div>
  );
}

const Info = ({ label, value }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest text-brand-soft/50 mb-0.5">{label}</div>
    <div className="text-sm text-brand-soft">{value || "—"}</div>
  </div>
);
